import React, { useMemo } from 'react';
import './AddEditReferralsRequest.AttorneyInformation.scss';
import ReadOnlyField from 'v3/Components/Form/ReadOnlyField';
import { TitleReadOnly } from 'v3/Components/Form/TitleReadOnly';
import { InputActive } from 'v3/Components/Form/InputActive';
import { FieldError } from 'v3/Components/Form/FormSection';
import {
  createReferralRequestSchema,
  useReferralRequestForm,
} from './useReferralRequestForm';
import { useIdParam, useLineOfBusinessParam } from '../../../../Utilities';
import { ReplicableComponent } from '../../Form/ReplicableComponent';
import { LineOfBusinessEnum } from 'v3/Models/Enums';
import { Render } from 'Components/Display';

type AttorneyInformationProps = {
  form: ReturnType<typeof useReferralRequestForm>;
  fieldErrors?: FieldError[];
};

export const AttorneyInformationActive: React.FC<AttorneyInformationProps> = ({
  form,
  fieldErrors,
}) => {
  const [id, isAdd, isEdit] = useIdParam();
  const { lob } = useLineOfBusinessParam();
  const isWorkersComp = lob === LineOfBusinessEnum.WorkersComp.toString();
  const isIME = lob === LineOfBusinessEnum.IME.toString();
  const isDisability = lob === LineOfBusinessEnum.Disability.toString();
  const isHealthReview = lob === LineOfBusinessEnum.HealthReview.toString();
  const isPharmacy = lob === LineOfBusinessEnum.Pharmacy.toString();
  const isImpairmentRatingReview =
    lob === LineOfBusinessEnum.ImpairmentRatingReview.toString();
  const attorneys = form.getFieldProps('attorneys').value;

  const ReferralRequestSchema = useMemo(
    () => createReferralRequestSchema(Number(lob) as LineOfBusinessEnum),
    [lob]
  );

  return (
    <div className="contact-row">
      <Render
        condition={
          isWorkersComp ||
          isIME ||
          isDisability ||
          isHealthReview ||
          isPharmacy ||
          isImpairmentRatingReview
        }
      >
        <ReplicableComponent
          form={form}
          initialData={{
            attorneyName: '',
            primaryContact: '',
            additionalContact: '',
          }}
          label="Attorney Information"
          formPropsToClearOnRemoval={['attorneys']}
          initialDataArray={attorneys}
        >
          {({ index, data, onChange, onDuplicate }) => (
            <div className="contact-fields">
              <InputActive
                id="attorneyName"
                name="Attorney Name"
                label="Attorney Name"
                value={attorneys?.[index]?.attorneyName ?? ''}
                onChange={(e: any) =>
                  onChange({ attorneyName: e.target.value }, 'attorneys')
                }
                error={
                  fieldErrors?.find(
                    (f) => f.fieldId === `attorneys[${index}].AttorneyName`
                  )?.error
                }
              />
              <InputActive
                id="primaryContact"
                name="primaryContact"
                label="Primary Contact (Phone or Email)"
                value={attorneys?.[index]?.primaryContact ?? ''}
                onChange={(e: any) =>
                  onChange({ primaryContact: e.target.value }, 'attorneys')
                }
                error={
                  fieldErrors?.find(
                    (f) => f.fieldId === `attorneys[${index}].PrimaryContact`
                  )?.error
                }
              />
              <InputActive
                id="additionalContact"
                label="Additional Contact (Phone or Email)"
                name="additionalContact"
                value={attorneys?.[index]?.additionalContact ?? ''}
                onChange={(e: any) =>
                  onChange({ additionalContact: e.target.value }, 'attorneys')
                }
                error={
                  fieldErrors?.find(
                    (f) => f.fieldId === `attorneys[${index}].AdditionalContact`
                  )?.error
                }
              />
            </div>
          )}
        </ReplicableComponent>
      </Render>
    </div>
  );
};

export const AttorneyInformationReadOnly: React.FC<
  AttorneyInformationProps
> = ({ form }) => {
  const { lob } = useLineOfBusinessParam();
  const ReferralRequestSchema = useMemo(
    () => createReferralRequestSchema(Number(lob) as LineOfBusinessEnum),
    [lob]
  );
  const isWorkersComp = lob === LineOfBusinessEnum.WorkersComp.toString();
  const isIME = lob === LineOfBusinessEnum.IME.toString();
  const isDisability = lob === LineOfBusinessEnum.Disability.toString();
  const isHealthReview = lob === LineOfBusinessEnum.HealthReview.toString();
  const isPharmacy = lob === LineOfBusinessEnum.Pharmacy.toString();
  const isImpairmentRatingReview =
    lob === LineOfBusinessEnum.ImpairmentRatingReview.toString();
  const attorneys = form.getFieldProps('attorneys').value;
  return (
    <div className="read-only-content">
      <Render
        condition={
          isWorkersComp ||
          isIME ||
          isDisability ||
          isHealthReview ||
          isPharmacy ||
          isImpairmentRatingReview
        }
      >
        <TitleReadOnly>Attorney Info</TitleReadOnly>
        {attorneys?.map((attorney: any, index: number) => (
          <div key={index}>
            <ReadOnlyField
              id="attorneyName"
              schema={ReferralRequestSchema}
              forceLabel="Attorney Name"
              value={attorney.attorneyName ?? ''}
            />
            <ReadOnlyField
              id="primaryContact"
              schema={ReferralRequestSchema}
              forceLabel="Primary Contact"
              value={attorney.primaryContact ?? ''}
            />
            <ReadOnlyField
              id="additionalContact"
              schema={ReferralRequestSchema}
              forceLabel="Additional Contact"
              value={attorney.additionalContact ?? ''}
            />
          </div>
        ))}
      </Render>
    </div>
  );
};
